import { ReactElement, ReactNode } from "react";

import { EnumViewMode } from "@/enums/view-mode";
import { ViewType } from "@/types/search.types";
import { cn } from "@/utils/cn";

interface ObjectListProps {
  isEmpty?: boolean;
  children: ReactNode;
}

interface ItemsProps extends React.HTMLAttributes<HTMLUListElement> {
  children: ReactNode;
  viewMode: ViewType;
}

const Items = ({ children, viewMode, className }: ItemsProps) => {
  // For TABLE view mode, render the children directly without grid wrapper
  if (viewMode === EnumViewMode.TABLE) {
    return <div className={cn("w-full", className)}>{children}</div>;
  }

  return (
    <ul
      className={cn(
        "grid gap-y-7 gap-x-5 md:gap-y-8 xl:gap-x-8 py-[3rem] md:py-[4rem] xl:py-[5rem]",
        viewMode === EnumViewMode.GRID &&
          "md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4",
        viewMode === EnumViewMode.LIST && "xl:grid-cols-2",
        className,
      )}
    >
      {children}
    </ul>
  );
};

const Empty = ({ children }: { children: ReactNode }) => (
  <div className="flex flex-col items-center justify-center py-[5rem]">
    {children}
  </div>
);

export const ObjectList = ({
  children,
  isEmpty = false,
}: ObjectListProps): ReactElement<any> => {
  const childrenArray = Array.isArray(children) ? children : [children];

  const getByType = (type: any) =>
    childrenArray.find((child: any) => child && child.type === type);

  return isEmpty ? getByType(Empty) || null : getByType(Items) || null;
};

ObjectList.Items = Items;
ObjectList.Empty = Empty;
